import { Check, X, AlertTriangle, Phone, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Verdict = "yes" | "no" | "maybe";
const ROWS: {
  criterion: string;
  detail: string;
  diy: {
    v: Verdict;
    note: string;
  };
  generic: {
    v: Verdict;
    note: string;
  };
  gold: {
    v: Verdict;
    note: string;
  };
}[] = [{
  criterion: "Brand-matched remotes on hand",
  detail: "B&D, Merlin, Chamberlain, ATA, Steel-Line in stock",
  diy: {
    v: "no",
    note: "Order online, wait 3-7 days"
  },
  generic: {
    v: "maybe",
    note: "Often one universal only"
  },
  gold: {
    v: "yes",
    note: "All 8 brands on the van"
  }
}, {
  criterion: "Handles rolling-code pairing",
  detail: "Security+ 2.0, TrioCode 128, Tri-Tran sequences",
  diy: {
    v: "maybe",
    note: "If you can find the right tutorial"
  },
  generic: {
    v: "maybe",
    note: "Brand-dependent"
  },
  gold: {
    v: "yes",
    note: "Daily — every Aussie brand"
  }
}, {
  criterion: "Pairs Car HomeLink to motor",
  detail: "BMW, Audi, Tesla, Mercedes visor button",
  diy: {
    v: "no",
    note: "Most tutorials are US-spec"
  },
  generic: {
    v: "no",
    note: "Usually declined as 'too tricky'"
  },
  gold: {
    v: "yes",
    note: "Standard part of every visit"
  }
}, {
  criterion: "Erases unknown / old remotes",
  detail: "Factory-reset motor memory for new homeowners",
  diy: {
    v: "maybe",
    note: "Easy to wipe yours too, by accident"
  },
  generic: {
    v: "no",
    note: "Not offered, not explained"
  },
  gold: {
    v: "yes",
    note: "Always offered, free with any visit"
  }
}, {
  criterion: "Tests from the driveway",
  detail: "Confirms full range, not just from inside the garage",
  diy: {
    v: "no",
    note: "Hard to test alone"
  },
  generic: {
    v: "maybe",
    note: "Often skipped to save time"
  },
  gold: {
    v: "yes",
    note: "Tested before we leave site"
  }
}, {
  criterion: "Fixed all-in price upfront",
  detail: "Quoted on the phone before we leave the yard",
  diy: {
    v: "yes",
    note: "But factor failed attempts"
  },
  generic: {
    v: "no",
    note: "Hourly + parts mark-up surprise"
  },
  gold: {
    v: "yes",
    note: "Flat price, locked in by phone"
  }
}, {
  criterion: "Shows you how to add more later",
  detail: "DIY add-a-remote + change-PIN walkthrough",
  diy: {
    v: "yes",
    note: "You already are"
  },
  generic: {
    v: "no",
    note: "Callback = more income"
  },
  gold: {
    v: "yes",
    note: "Free 3-min walkthrough"
  }
}, {
  criterion: "1-Year Labour Warranty",
  detail: "Re-program at no cost if it fails inside 12 months",
  diy: {
    v: "no",
    note: "You're on your own"
  },
  generic: {
    v: "maybe",
    note: "30-day if you're lucky"
  },
  gold: {
    v: "yes",
    note: "Full 12 months, no fine print"
  }
}];
function VerdictCell({
  verdict,
  note,
  highlight
}: {
  verdict: Verdict;
  note: string;
  highlight?: boolean;
}) {
  const Icon = verdict === "yes" ? Check : verdict === "no" ? X : AlertTriangle;
  const iconColor = verdict === "yes" ? highlight ? GOLD : "#7BD389" : verdict === "no" ? "#E5535B" : "#E5B547";
  const bg = verdict === "yes" ? highlight ? `${GOLD}22` : "#7BD38922" : verdict === "no" ? "#E5535B22" : "#E5B54722";
  const border = verdict === "yes" ? highlight ? `${GOLD}55` : "#7BD38955" : verdict === "no" ? "#E5535B55" : "#E5B54755";
  return <div className="flex items-start gap-2.5">
      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-md flex-shrink-0 mt-0.5" style={{
      width: 22,
      height: 22,
      backgroundColor: bg,
      border: `1px solid ${border}`
    }}>
        <Icon size={12} color={iconColor} strokeWidth={3} />
      </span>
      <span style={{
      fontFamily: FONT_BODY,
      fontSize: "12.5px",
      lineHeight: 1.5,
      color: highlight ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.65)",
      fontWeight: highlight ? 600 : 500
    }}>
        {note}
      </span>
    </div>;
}
export default function RemoteComparison() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Ambient radial */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 50% 0%, ${NAVY_LIGHT}66 0%, ${NAVY} 60%), radial-gradient(circle at 50% 100%, ${GOLD}10 0%, transparent 50%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />

      {/* Watermark */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      left: "-30px",
      top: "40px",
      fontFamily: FONT_HEAD,
      fontSize: "240px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}12`
    }}>
        07
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD
          }}>
              07 · The Honest Comparison
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: "#ffffff",
          marginBottom: "20px"
        }}>
            DIY tutorial vs the generic mob
            <br />
            vs <span style={{
            color: GOLD
          }}>actually getting it done</span>.
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: "rgba(255,255,255,0.72)",
          maxWidth: "640px"
        }}>
            Eight things we wish someone had told us before our first remote
            job. Honest scorecard — no spin.
          </p>
        </div>

        {/* Comparison table */}
        <div className="rounded-2xl overflow-hidden" style={{
        backgroundColor: `${NAVY_DEEP}AA`,
        border: `1px solid rgba(255,255,255,0.08)`,
        boxShadow: "0 30px 80px rgba(0,0,0,0.4)"
      }}>
          {/* Header row */}
          <div className="grid grid-cols-[1.6fr_1fr_1fr_1.2fr] gap-0" style={{
          borderBottom: `1px solid rgba(255,255,255,0.1)`,
          backgroundColor: `${NAVY_LIGHT}33`
        }}>
            <div className="p-5 md:p-6">
              <span style={{
              fontFamily: FONT_HEAD,
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.5)"
            }}>
                Criterion
              </span>
            </div>
            <div className="p-5 md:p-6 text-center border-l" style={{
            borderColor: "rgba(255,255,255,0.08)"
          }}>
              <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "13px",
              fontWeight: 800,
              color: "rgba(255,255,255,0.6)",
              letterSpacing: "-0.01em",
              marginBottom: "2px"
            }}>
                DIY
              </div>
              <div style={{
              fontFamily: FONT_BODY,
              fontSize: "10px",
              color: "rgba(255,255,255,0.4)",
              letterSpacing: "0.08em",
              textTransform: "uppercase"
            }}>
                YouTube tutorial
              </div>
            </div>
            <div className="p-5 md:p-6 text-center border-l" style={{
            borderColor: "rgba(255,255,255,0.08)"
          }}>
              <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "13px",
              fontWeight: 800,
              color: "rgba(255,255,255,0.6)",
              letterSpacing: "-0.01em",
              marginBottom: "2px"
            }}>
                Generic Operator
              </div>
              <div style={{
              fontFamily: FONT_BODY,
              fontSize: "10px",
              color: "rgba(255,255,255,0.4)",
              letterSpacing: "0.08em",
              textTransform: "uppercase"
            }}>
                Locksmith / handyman
              </div>
            </div>
            <div className="p-5 md:p-6 text-center border-l relative" style={{
            borderColor: `${GOLD}55`,
            backgroundColor: `${GOLD}10`
          }}>
              {/* Gold ribbon */}
              <span className="absolute" style={{
              top: "-1px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "70%",
              height: "3px",
              background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`
            }} />
              <div className="flex items-center justify-center gap-1.5 mb-0.5">
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <Sparkles size={12} color={GOLD} strokeWidth={2.5} />
                </span>
                <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "13px",
                fontWeight: 900,
                color: GOLD,
                letterSpacing: "-0.01em"
              }}>
                  Gold Garage Door
                </div>
              </div>
              <div style={{
              fontFamily: FONT_BODY,
              fontSize: "10px",
              color: "rgba(255,255,255,0.55)",
              letterSpacing: "0.08em",
              textTransform: "uppercase"
            }}>
                Your local crew
              </div>
            </div>
          </div>

          {/* Rows */}
          {ROWS.map((row, i) => <div key={row.criterion} className="grid grid-cols-[1.6fr_1fr_1fr_1.2fr] gap-0" style={{
          borderBottom: i === ROWS.length - 1 ? "none" : `1px solid rgba(255,255,255,0.06)`
        }}>
              {/* Criterion */}
              <div className="p-5 md:p-6">
                <h4 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 700,
              fontSize: "14.5px",
              color: "#ffffff",
              marginBottom: "4px",
              lineHeight: 1.25,
              letterSpacing: "-0.01em"
            }}>
                  {row.criterion}
                </h4>
                <p style={{
              fontFamily: FONT_BODY,
              fontSize: "12px",
              color: "rgba(255,255,255,0.5)",
              lineHeight: 1.5
            }}>
                  {row.detail}
                </p>
              </div>

              {/* DIY */}
              <div className="p-5 md:p-6 border-l" style={{
            borderColor: "rgba(255,255,255,0.06)"
          }}>
                <VerdictCell verdict={row.diy.v} note={row.diy.note} />
              </div>

              {/* Generic */}
              <div className="p-5 md:p-6 border-l" style={{
            borderColor: "rgba(255,255,255,0.06)"
          }}>
                <VerdictCell verdict={row.generic.v} note={row.generic.note} />
              </div>

              {/* Gold — highlighted */}
              <div className="p-5 md:p-6 border-l relative" style={{
            borderColor: `${GOLD}33`,
            backgroundColor: `${GOLD}08`
          }}>
                <VerdictCell verdict={row.gold.v} note={row.gold.note} highlight />
              </div>
            </div>)}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "15px",
          lineHeight: 1.6,
          color: "rgba(255,255,255,0.7)",
          maxWidth: "560px"
        }}>
            <span style={{
            color: GOLD,
            fontFamily: FONT_HEAD,
            fontWeight: 700
          }}>
              The honest truth:
            </span>{" "}
            for one or two remotes, the value gap between DIY and a proper
            programmer is huge — and we'll quote you flat over the phone before
            you commit.
          </p>

          <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
            <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full transition-all hover:scale-[1.02]" style={{
            backgroundColor: GOLD,
            color: NAVY,
            fontFamily: FONT_HEAD,
            fontWeight: 700,
            fontSize: "14px",
            boxShadow: `0 8px 24px ${GOLD}33`
          }}>
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <Phone size={16} strokeWidth={2.5} />
              </span>
              Get a flat-price quote
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <ArrowRight size={14} strokeWidth={2.5} />
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}